import React, { memo } from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, ArrowLeft, Edit2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface InputOutputNodeData {
  type: 'input' | 'output';
  label: string;
  schema?: Record<string, any>;
  onEdit?: () => void;
}

function InputOutputNode({ data, selected }: NodeProps<InputOutputNodeData>) {
  const isInput = data.type === 'input';
  const color = isInput ? 'green' : 'orange';

  return (
    <div
      className={`bg-white rounded-lg border-2 shadow-lg min-w-[200px] max-w-[280px] ${
        selected ? `border-${color}-500 shadow-${color}-200` : `border-${color}-300`
      }`}
    >
      {/* Input Handle (only for output nodes) */}
      {!isInput && (
        <Handle
          type="target"
          position={Position.Left}
          className={`w-3 h-3 !bg-${color}-500`}
        />
      )}

      {/* Header */}
      <div className={`bg-gradient-to-r from-${color}-500 to-${color}-600 text-white px-3 py-2 rounded-t-md flex items-center justify-between`}>
        <div className="flex items-center gap-2">
          {isInput ? <ArrowRight className="w-4 h-4" /> : <ArrowLeft className="w-4 h-4" />}
          <span className="font-semibold text-sm">{isInput ? 'Input' : 'Output'}</span>
        </div>
        {data.onEdit && (
          <Button
            size="sm"
            variant="ghost"
            className={`h-6 w-6 p-0 hover:bg-${color}-600`}
            onClick={data.onEdit}
          >
            <Edit2 className="w-3 h-3" />
          </Button>
        )}
      </div>

      {/* Content */}
      <div className="p-3 space-y-2">
        <div className="font-semibold text-sm text-gray-900">{data.label}</div>

        {data.schema && (
          <div className="pt-2 border-t">
            <div className="text-xs text-gray-500">
              {Object.keys(data.schema).length} field(s)
            </div>
          </div>
        )}
      </div>

      {/* Output Handle (only for input nodes) */}
      {isInput && (
        <Handle
          type="source"
          position={Position.Right}
          className={`w-3 h-3 !bg-${color}-500`}
        />
      )}
    </div>
  );
}

export default memo(InputOutputNode);

